import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { inr0, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Download } from "lucide-react";

export default function TenantSummary() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/reports/tenant-summary").then(setRows).catch(() => {}); }, []);

  const exportCsv = () => {
    const head = ["Tenant", "Code", "GSTIN", "Property", "Unit", "Move-in", "Rent", "Deposit", "Maintenance", "Latest Electricity", "Dues"];
    const lines = rows.map((r) => [r.tenant, r.tenant_code, r.gstin, r.property, r.unit, r.move_in, r.rent, r.deposit, r.maintenance, r.latest_electricity, r.dues].map((v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`).join(","));
    const csv = [head.join(","), ...lines].join("\n");
    const url = window.URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "tenant-summary.csv"; a.click(); window.URL.revokeObjectURL(url);
  };
  const printPdf = () => window.print();

  const totals = rows.reduce((a, r) => ({ rent: a.rent + r.rent, deposit: a.deposit + r.deposit, maint: a.maint + r.maintenance, dues: a.dues + r.dues }), { rent: 0, deposit: 0, maint: 0, dues: 0 });

  return (
    <div>
      <PageHeader title="Tenant Summary Sheet" subtitle={`${rows.length} active tenants — rent, deposit, maintenance, electricity & dues`} testid="tenant-summary-header">
        <Button variant="outline" onClick={printPdf} data-testid="ts-pdf"><Download className="w-4 h-4 mr-1" /> Print / PDF</Button>
        <Button onClick={exportCsv} data-testid="ts-excel" className="bg-indigo-600 hover:bg-indigo-700"><Download className="w-4 h-4 mr-1" /> Export Excel</Button>
      </PageHeader>
      <Card className="border-slate-200 shadow-sm overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-slate-50"><TableHead>Tenant</TableHead><TableHead>Property / Unit</TableHead><TableHead>Move-in</TableHead><TableHead className="text-right">Rent</TableHead><TableHead className="text-right">Deposit</TableHead><TableHead className="text-right">Maintenance</TableHead><TableHead className="text-right">Latest Electricity</TableHead><TableHead className="text-right">Dues</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-8">No active tenants yet.</TableCell></TableRow>}
            {rows.map((r, i) => (
              <TableRow key={i} data-testid={`ts-row-${i}`} className="hover:bg-slate-50/50">
                <TableCell><div className="font-medium text-slate-800">{r.tenant || "—"}</div><div className="text-xs text-slate-400">{r.tenant_code}{r.gstin ? ` · ${r.gstin}` : ""}</div></TableCell>
                <TableCell className="text-sm">{r.property || "—"}{r.unit ? ` · ${r.unit}` : ""}</TableCell>
                <TableCell className="text-xs">{r.move_in ? fmtDate(r.move_in) : "—"}</TableCell>
                <TableCell className="text-right">{inr0(r.rent)}</TableCell>
                <TableCell className="text-right">{inr0(r.deposit)}</TableCell>
                <TableCell className="text-right">{inr0(r.maintenance)}</TableCell>
                <TableCell className="text-right">{inr0(r.latest_electricity)}</TableCell>
                <TableCell className={`text-right font-semibold ${r.dues > 0 ? "text-rose-600" : "text-emerald-600"}`}>{inr0(r.dues)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
      {rows.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-6 text-sm text-slate-600">
          <span>Total Rent: <b>{inr0(totals.rent)}</b></span>
          <span>Total Deposit: <b>{inr0(totals.deposit)}</b></span>
          <span>Total Maintenance: <b>{inr0(totals.maint)}</b></span>
          <span>Total Dues: <b className="text-rose-600">{inr0(totals.dues)}</b></span>
        </div>
      )}
    </div>
  );
}
